import { useMutation, useQuery } from "react-query";
import { botSecureAxios } from "../bot";
import { queryClient } from "../../_helpers/query";
import { useItemsFine } from "../app/items";

// fetching the plans
export const usePlans = () => {
    return useItemsFine("qfin_subscription_plans", "sort=price&fields=*", true);
};

// Fetching organization trial status
export const useOrganizationTrialStatus = (organizationId) => {
    const query = useItemsFine(
        "qdb_organization",
        `filter[id][_eq]=${organizationId}&fields=trial`,
        !!organizationId,
    );
    return {
        ...query,
        data: query.data?.[0],
    };
};

export const useTokenBalance = (orgId) => {
    const query = useItemsFine(
        "qdb_organization",
        `filter[id][_eq]=${orgId}&fields=token_plan.count`,
        !!orgId,
    );
    return {
        ...query,
        data: query.data?.[0]?.token_plan?.count ?? null,
    };
};

export const useSubscription = (organizationId, polling = false) => {
    const query = useItemsFine(
        "qfin_subscriptions",
        `filter[_or][0][is_active][_eq]=true&filter[_or][1][status][_eq]=active&filter[_or][2][status][_eq]=authenticated&filter[_or][3][status][_eq]=created&filter[organization][_eq]=${organizationId}&sort=-date_created&limit=1&fields=*,payment_plan.*`,
        !!organizationId,
        polling ? 5 : false,
    );
    return {
        ...query,
        data: query.data?.[0],
    };
};

// Fetch transactions for the current organization
export const useTransactions = (organizationId) => {
    return useItemsFine(
        "qfin_transaction",
        `filter[organization][_eq]=${organizationId}&sort=-date_created&fields=*`,
        !!organizationId,
    );
};

// Create a new subscription
export const useCreateSubscription = (onSuccess, onError) => {
    return useMutation(
        (data) => {
            return botSecureAxios
                .post("/subscription/create", data)
                .then((res) => res.data);
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

// Verify payment
export const useVerifyPayment = (onSuccess, onError) => {
    return useMutation(
        ({
            razorpay_payment_id,
            razorpay_subscription_id,
            razorpay_signature,
        }) => {
            console.log("Verifying subscription payment (Mutation Start):", {
                razorpay_payment_id,
                razorpay_subscription_id,
                razorpay_signature,
            });
            return botSecureAxios
                .post(`/subscription/verify-payment`, {
                    razorpay_payment_id,
                    razorpay_subscription_id,
                    razorpay_signature,
                })
                .then((res) => {
                    console.log("Verify payment response:", res.data);
                    return res.data;
                });
        },
        {
            onSuccess: (data) => {
                // setTimeout(() => {
                //     queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                //     queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                //     queryClient.invalidateQueries(["items", "qfin_transaction"]);

                queryClient.refetchQueries(["items", "qfin_subscriptions"], {
                    active: true,
                });
                queryClient.refetchQueries(["items", "qdb_token_plan"], {
                    active: true,
                });
                queryClient.refetchQueries(["items", "qfin_transaction"], {
                    active: true,
                });
                // }, 1000);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                console.error("Payment verification failed:", error);
                console.error("Error response:", error.response?.data);
                if (onError) onError(error);
            },
        },
    );
};
export const useCancelSubscription = (
    onSuccess = () => {},
    onError = () => {},
) => {
    return useMutation(
        ({ subscription_id }) =>
            botSecureAxios
                .post(`/subscription/${subscription_id}/cancel`, {
                    cancel_immediately_with_refund_check: false,
                })
                .then((res) => res.data),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

export const useRestartSubscription = (
    onSuccess = () => {},
    onError = () => {},
) => {
    return useMutation(
        ({ subscription_id }) =>
            botSecureAxios
                .post(`/subscription/${subscription_id}/cancel_revoke`, {})
                .then((res) => res.data),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

// Pause subscription
export const usePauseSubscription = (
    onSuccess = () => {},
    onError = () => {},
) => {
    return useMutation(
        ({ subscription_id }) =>
            botSecureAxios
                .post(`/subscription/${subscription_id}/pause`, {
                    pause_at: "now",
                })
                .then((res) => res.data),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

// Resume subscription
export const useResumeSubscription = (
    onSuccess = () => {},
    onError = () => {},
) => {
    return useMutation(
        ({ subscription_id }) =>
            botSecureAxios
                .post(`/subscription/${subscription_id}/resume`, {
                    resume_at: "now",
                })
                .then((res) => res.data),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

// Upgrade subscription
export const useUpgradeSubscription = (
    onSuccess = () => {},
    onError = () => {},
) => {
    return useMutation(
        ({ subscription_id, new_plan_id }) =>
            botSecureAxios
                .patch(`/subscription/${subscription_id}/upgrade`, {
                    new_plan_id,
                    when: "next_cycle",
                })
                .then((res) => res.data),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

// Preview subscription price
export const usePreviewSubscriptionPrice = (onSuccess, onError) => {
    return useMutation(
        (data) => {
            return botSecureAxios
                .post("/subscription/price-preview", data)
                .then((res) => res.data);
        },
        {
            onSuccess: (data) => {
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

// Create a trial subscription
export const useCreateTrialSubscription = (onSuccess) => {
    return useMutation(
        (data) => {
            return botSecureAxios
                .post("/subscription/trial", data)
                .then((res) => res.data);
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(["items", "qfin_subscriptions"]);
                queryClient.invalidateQueries(["items", "qdb_token_plan"]);
                if (onSuccess) onSuccess(data);
            },
        },
    );
};
// Verify one-time payment for subscription
export const useVerifyOneTimePayment = (onSuccess, onError) => {
    return useMutation(
        (data) => {
            console.log("Verifying one-time payment (Mutation Start):", data);

            // Remap keys to match backend requirements
            const payload = {
                razorpay_payment_id: data.payment_id,
                razorpay_order_id: data.order_id,
                razorpay_signature: data.signature,
                ...data,
            };

            return botSecureAxios
                .post(`/subscription/one_time_verify_payment`, payload)
                .then((res) => {
                    console.log("Verify one-time payment response:", res.data);
                    return res.data;
                });
        },
        {
            onSuccess: (data) => {
                queryClient.refetchQueries(["items", "qfin_subscriptions"], {
                    active: true,
                });
                queryClient.refetchQueries(["items", "qdb_token_plan"], {
                    active: true,
                });
                queryClient.refetchQueries(["items", "qfin_transaction"], {
                    active: true,
                });
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                console.error("One-time payment verification failed:", error);
                if (onError) onError(error);
            },
        },
    );
};

export const useCreateTopup = (onSuccess, onError) => {
    return useMutation(
        (data) => {
            // data: { org_id, account_id, preset }
            return botSecureAxios
                .post("/subscription/topup/create", data)
                .then((res) => res.data);
        },
        {
            onSuccess: (data) => {
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};

export const useVerifyTopup = (onSuccess, onError) => {
    return useMutation(
        (data) => {
            // data: { razorpay_payment_id, razorpay_order_id, razorpay_signature }
            return botSecureAxios
                .post("/subscription/topup/verify", data)
                .then((res) => res.data);
        },
        {
            onSuccess: (data) => {
                queryClient.refetchQueries(["items", "qdb_token_plan"], {
                    active: true,
                });
                queryClient.refetchQueries(["items", "qfin_transaction"], {
                    active: true,
                });
                queryClient.refetchQueries(["items", "qdb_organization"], {
                    active: true,
                });
                if (onSuccess) onSuccess(data);
            },
            onError: (error) => {
                if (onError) onError(error);
            },
        },
    );
};
